import React, { useState, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import NintendoModel from '../components/NintendoModel';
import './LaptopViewer.css';
import { FaWhatsapp } from "react-icons/fa";
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal, Button, Form } from 'react-bootstrap';
import logo from '../assets/TT logo.png';
import "driver.js/dist/driver.css";
import { driver } from "driver.js";
import { Link } from 'react-router-dom';

// Map raw model part names → user-friendly aliases
const partNameAliases = {
  "Display_Screen_-_1": "Display Screen",
  "Display_Touch_Screen_-_2": "Touch Screen",
  "Left_Joycon_-3": "Left Joycon",
  "Right_Joycon_-_4": "Right Joycon",
  "Charging_port_-_5": "Charging Port",
  "Power_Switch_-_6": "Power Button",
  "Volume_Button_-_7": "Volume Button",
  "Fan_-_8": "Fan",
  "Dock_USB_port_A_-_9": "Dock USB Port A",
  "Dock_USB_port_B_-_10": "Dock USB Port B",
  "Dock_Not_working_-11": "Dock (Main Board)",
  "Not_working_-_12": "Unknown/Other Board",
  "Dock_USB_-_13": "Dock USB",
  "Dock_charging_port_-_14": "Dock Charging Port",
  "Dock_HDMI_port_-_15": "Dock HDMI Port",
  "SD_card_slot_-_16": "SD Card Slot",
  "Wifi_Not_working_-_17": "WiFi Module",
  "Connectivity_issue_-_18": "Connectivity Module",
  "Power_issue_-_19": "Motherboard / Power Issue"
};

// Issues mapped to ALIAS (not raw names)
const partIssues = {
  "Display Screen": [
    "Screen flickers",
    "LCD or OLED broken",
    "No display on boot",
    "Black screen randomly",
    "Dim screen at full brightness",
    "Intermittent display",
    "Lines across the screen",
    "Screen turns off during use",
    "External monitor not detected",
    "Cracked or broken screen",
    "Colour distortion or flicker",
    "Screen ghosting on movement"
  ],
  "Touch Screen": [
    "Touchscreen not working",
    "Unresponsive to taps or swipes",
    "Ghost touches (registering touches on its own)"
  ],
  "Left Joycon": [
    "Joycon not detected",
    "Stick drift",
    "Buttons not working"
  ],
  "Right Joycon": [
    "Joycon not detected",
    "Stick drift",
    "Buttons not working"
  ],
  "Charging Port": [
    "Console not charging",
    "Charger not recognized",
    "Battery drains fast",
    "Battery not detecting",
    "Loose or broken charging port",
    "Charging only at a certain angle",
    "Power adapter overheating",
    "USB-C charging not working",
    "Slow charging",
    "No charging light indicator",
    "Charging stops randomly",
    "No HDMI output through charging port"
  ],
  "Power Button": [
    "Button stuck",
    "Not responsive",
    "Console not powering ON"
  ],
  "Volume Button": [
    "Volume up not working",
    "Volume down not working",
    "Button stuck"
  ],
  "Fan": [
    "Console gets too hot",
    "Fans running loud constantly",
    "Fan not spinning",
    "Console shuts off when hot",
    "Fan error"
  ],
  "Dock USB Port A": [
    "USB device not detected",
    "Loose or broken port"
  ],
  "Dock USB Port B": [
    "USB device not detected",
    "Loose or broken port"
  ],
  "Dock (Main Board)": [
    "Dock not working",
    "No video output via HDMI"
  ],
  "Unknown/Other Board": [
    "Component failure",
    "Not responding"
  ],
  "Dock USB": [
    "USB not working",
    "Connection unstable"
  ],
  "Dock Charging Port": [
    "Not charging while docked",
    "Loose or broken port"
  ],
  "Dock HDMI Port": [
    "No HDMI output",
    "Loose or broken port"
  ],
  "SD Card Slot": [
    "SD card not detected",
    "Gaming card not detected",
    "Card reader failure"
  ],
  "WiFi Module": [
    "WiFi not working",
    "Connection drops",
    "Cannot detect networks"
  ],
  "Connectivity Module": [
    "Bluetooth not working",
    "Connection unstable",
    "Controller not pairing"
  ],
  "Motherboard / Power Issue": [
    "Liquid spill on board",
    "Not powering ON",
    "Short circuit signs",
    "Burnt smell or heat spots",
    "Joycon not detecting",
    "Faulty IC",
    "USB ports not working",
    "C port no video output",
    "C port failure",
    "NAND corrupted",
    "Black screen",
    "Blue screen",
    "Red screen",
    "Stuck at logo",
    "No sound from speakers",
    "Distorted or crackling audio",
    "Headphones not detected",
    "Audio jack loose or broken"
  ]
};


const NintendoSwitch = () => {
  const [selectedPart, setSelectedPart] = useState(null); // RAW name
  const [hoveredPart, setHoveredPart] = useState(null);   // RAW name
  const [modalShow, setModalShow] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });

  // Convert raw name → alias for UI
  const getAlias = (rawName) => partNameAliases[rawName] || rawName;

  const hoveredAlias = getAlias(hoveredPart);
  const selectedAlias = getAlias(selectedPart);

  const initialCameraPosition = [0, 0.6, 1 ];

  // Store RAW name for highlight logic
  const handlePartClick = (rawName) => {
    setSelectedPart(rawName);
  };

  const handleIssueClick = (issue) => {
    setModalContent(issue);
    setModalShow(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    const message = `Hello, I want to report an issue:
- Device: Nintendo Switch
- Part Name: ${selectedAlias || "Not Selected"}
- Issue: ${modalContent}
- Name: ${formData.name}
- Email: ${formData.email}
- Phone: ${formData.phone}
- Location: ${formData.location}
- Issue Description: ${formData.issue}`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappNumber = "971567756193"; // ✅ replace with your WhatsApp number

    // Works on both WhatsApp mobile app & WhatsApp Web (desktop)
    const url = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodedMessage}`;

    window.open(url, "_blank");

    setModalShow(false);
    setFormData({ name: "", email: "", phone: "" });
  };

  // ✅ Guided tour with driver.js
  const startTour = () => {
    const tourDriver = driver({
      showProgress: true,
      showButtons: ["next", "previous", "close"],
      steps: [
        {
          element: ".model-canvas",
          popover: {
            title: "3D Laptop Model",
            description: "👉 Rotate and zoom the product with your mouse or touchpad.",
          },
        },
        {
          element: ".sidebar",
          popover: {
            title: "Parts List",
            description: "Click a part from this list or directly on the product model.",
          },
        },
        {
          element: ".custom-modal",
          popover: {
            title: "Submit an Issue",
            description: "Choose an issue and send it via WhatsApp for quick support!",
          },
        },
      ],
    });

    tourDriver.drive();
  };

  useEffect(() => {
    const hasSeenTour = localStorage.getItem("hasSeenTour");
    if (!hasSeenTour) {
      startTour();
      localStorage.setItem("hasSeenTour", "true");
    }
  }, []);


  return (
    <>
      <header className="top-header d-flex justify-content-between align-items-center px-3 py-2">
        <Link to={'/'}><img src={logo} style={{ height: '160px', margin: '-40px -30px', padding: '0px' }} /></Link>
        <Button variant="info" onClick={startTour}>
          📖 Show Instructions
        </Button>
      </header>
      <div className="laptop-viewer-container">
        <div className="model-canvas">
          <Canvas camera={{ position: initialCameraPosition, fov: 50 }}>
            <ambientLight />
            <directionalLight position={[10, 10, 5]} />
            <NintendoModel
              onPartClick={handlePartClick}
              onHover={setHoveredPart}
              selectedPart={selectedPart} // RAW name
            />
            <OrbitControls />
          </Canvas>
          {hoveredPart && <div className="tooltip">{hoveredAlias}</div>}
        </div>

        <div className="sidebar">
          {!selectedPart && (
            <>
              <h3>All Parts</h3>
              <ul>
                {Object.entries(partNameAliases).map(([rawName, alias], i) => (
                  <li
                    key={i}
                    onClick={() => handlePartClick(rawName)} // RAW name
                    style={{ cursor: 'pointer' }}
                  >
                    {alias}
                  </li>
                ))}
              </ul>
            </>
          )}

          {selectedPart && (
            <>
              <h3>{selectedAlias} Issues</h3>
              <ul>
                {(partIssues[selectedAlias] || []).map((issue, i) => (
                  <li
                    key={i}
                    onClick={() => handleIssueClick(issue)}
                    style={{ cursor: 'pointer' }}
                  >
                    {issue}
                  </li>
                ))}
              </ul>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedPart(null)}
              >
                🔙 Back to Parts
              </Button>
            </>
          )}
        </div>
      </div>

      <Modal show={modalShow} onHide={() => setModalShow(false)} centered className="custom-modal">
        <Modal.Header style={{ color: 'white' }} closeButton>
          <Modal.Title>{modalContent}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="formName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formPhone">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="tel"
                placeholder="Enter phone number"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formLocation">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formEmergency">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                placeholder="Urgency Level"
                name="Emergency"
                value={formData.Emergency}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formIssue">
              <Form.Label>Describe Issue</Form.Label>
              <Form.Control
                as="textarea"
                rows={4} // You can adjust the number of rows as needed
                placeholder="Enter Issue Description"
                name="issue"
                value={formData.issue}
                onChange={handleInputChange}
              />
            </Form.Group>

          </Form>
          <Button variant="success" onClick={handleSubmit} className="d-flex align-items-center gap-2">
            <FaWhatsapp size={20} />
            Submit Issue on WhatsApp
          </Button>

        </Modal.Body>
        <Modal.Footer>

        </Modal.Footer>
      </Modal>
    </>
  );
};

export default NintendoSwitch;
